import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, Edit, Trash } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"

import { TransactionList } from "@/components/finance"
import { DeletePersonDialog } from "@/components/people/delete-person-dialog"
import { PersonForm } from "@/components/people/person-form"
import { PersonTypeBadge } from "@/components/people/person-type-badge"
import { PersonDetailsSkeleton } from "@/components/skeletons/person-details-skeleton"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOptimizedRefetch } from "@/hooks/use-optimized-refetch"
import { listTransactionsByPerson } from "@/http/finance"
import { getPerson } from "@/http/people"
import { useOrganization } from "@/store/organization"

type TPersonDetailsAction = "edit" | "delete"

type TPersonDetailsSearch = {
  action?: TPersonDetailsAction
}

const personQueryOptions = (personId: string, orgSlug: string) =>
  queryOptions({
    queryKey: ["person", personId, orgSlug],
    queryFn: async () => {
      const response = await getPerson({
        id: personId,
        organizationSlug: orgSlug,
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      return response.data
    },
  })

const personTransactionsQueryOptions = (personId: string, orgSlug: string) =>
  queryOptions({
    queryKey: ["person-transactions", personId, orgSlug],
    queryFn: async () => {
      const response = await listTransactionsByPerson(orgSlug, personId)

      if (response.error) {
        throw new Error(response.error.message)
      }

      return response.data
    },
  })

/**
 * Person details route
 */
export const Route = createFileRoute(
  "/_authenticated/$orgSlug/people/$personId",
)({
  validateSearch: (search: Record<string, unknown>): TPersonDetailsSearch => ({
    action:
      search.action === "edit" || search.action === "delete"
        ? (search.action as TPersonDetailsAction)
        : undefined,
  }),
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(
        personQueryOptions(params.personId, params.orgSlug),
      ),
      context.queryClient.ensureQueryData(
        personTransactionsQueryOptions(params.personId, params.orgSlug),
      ),
    ])
  },
  component: PersonDetails,
  pendingComponent: PersonDetailsSkeleton,
})

function PersonDetails() {
  const { personId, orgSlug } = Route.useParams()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { currentOrg } = useOrganization()
  const optimizedRefetch = useOptimizedRefetch()
  const editDialogOpen = search.action === "edit"
  const deleteDialogOpen = search.action === "delete"

  const setAction = (action?: TPersonDetailsAction) => {
    navigate({
      search: (prev) => {
        if (prev.action === action) {
          return prev
        }
        const { action: _action, ...rest } = prev
        return action ? { ...rest, action } : { ...rest, action: undefined }
      },
    })
  }

  // Fetch person data
  const { data: person, refetch } = useSuspenseQuery(
    personQueryOptions(personId, orgSlug),
  )

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    refetch: refetchTransactions,
  } = useSuspenseQuery(personTransactionsQueryOptions(personId, orgSlug))

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }),
    [],
  )

  const {
    paidIncome,
    paidOutcome,
    paidBalance,
    predictedIncome,
    predictedOutcome,
    predictedBalance,
  } = useMemo(() => {
    const totals = {
      paidIncome: 0,
      paidOutcome: 0,
      paidBalance: 0,
      predictedIncome: 0,
      predictedOutcome: 0,
      predictedBalance: 0,
    }

    for (const transaction of transactionsData?.transactions || []) {
      const isPaid = transaction.isPaid === true

      // Paid transactions
      if (isPaid) {
        if (transaction.type === "income") {
          totals.paidIncome += transaction.value
        } else if (transaction.type === "outcome") {
          totals.paidOutcome += transaction.value
        }
      }

      // Predicted (unpaid) transactions
      if (!isPaid) {
        if (transaction.type === "income") {
          totals.predictedIncome += transaction.value
        } else if (transaction.type === "outcome") {
          totals.predictedOutcome += transaction.value
        }
      }
    }

    totals.paidBalance = totals.paidIncome - totals.paidOutcome
    totals.predictedBalance = totals.predictedIncome - totals.predictedOutcome

    return totals
  }, [transactionsData?.transactions])

  if (!currentOrg || !person) {
    return null
  }

  const handleEditSuccess = () => {
    setAction(undefined)
    refetch()
    toast.success("Pessoa atualizada com sucesso!")
  }

  const handleDeleteSuccess = () => {
    toast.success("Pessoa deletada com sucesso!")
    navigate({
      to: "/$orgSlug/people",
      params: { orgSlug: currentOrg.slug },
    })
  }

  const canDelete = currentOrg.role === "admin"

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({
                to: "/$orgSlug/people",
                params: { orgSlug: currentOrg.slug },
              })
            }
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
              {person.firstName} {person.lastName}
            </h1>
            <div className="flex gap-2 mt-2">
              <PersonTypeBadge type={person.personType} />
              {person.isCustomer && <Badge variant="default">Cliente</Badge>}
              {person.isSupplier && (
                <Badge variant="secondary">Fornecedor</Badge>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAction("edit")}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          {canDelete && (
            <DeletePersonDialog
              trigger={
                <Button variant="destructive">
                  <Trash className="h-4 w-4 mr-2" />
                  Deletar
                </Button>
              }
              open={deleteDialogOpen}
              onOpenChange={(nextOpen) =>
                setAction(nextOpen ? "delete" : undefined)
              }
              personId={person.id}
              personName={`${person.firstName} ${person.lastName}`}
              organizationSlug={currentOrg.slug}
              onSuccess={handleDeleteSuccess}
            />
          )}
        </div>
      </div>

      {/* Person Details */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle>Informações de Contato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-sm">
                {person.email || (
                  <span className="text-muted-foreground">-</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Telefone
              </p>
              <p className="text-sm">
                {person.phoneDDI && person.phoneNumber ? (
                  `+${person.phoneDDI} ${person.phoneNumber}`
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Website
              </p>
              <p className="text-sm">
                {person.website ? (
                  <a
                    href={person.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {person.website}
                  </a>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle>Endereço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Endereço
              </p>
              <p className="text-sm">
                {person.address || (
                  <span className="text-muted-foreground">-</span>
                )}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Cidade
                </p>
                <p className="text-sm">
                  {person.city || (
                    <span className="text-muted-foreground">-</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Estado
                </p>
                <p className="text-sm">
                  {person.state || (
                    <span className="text-muted-foreground">-</span>
                  )}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">CEP</p>
                <p className="text-sm">
                  {person.zipCode || (
                    <span className="text-muted-foreground">-</span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  País
                </p>
                <p className="text-sm">
                  {person.country || (
                    <span className="text-muted-foreground">-</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Informações Adicionais</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                CPF/CNPJ
              </p>
              <p className="text-sm">
                {person.taxId || (
                  <span className="text-muted-foreground">-</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Observações
              </p>
              <p className="text-sm whitespace-pre-wrap">
                {person.notes || (
                  <span className="text-muted-foreground">-</span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Resumo financeiro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Total */}
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-3">
                  Total
                </p>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Total de entrada
                    </p>
                    <p className="text-lg font-semibold text-emerald-600 tabular-nums">
                      {currencyFormatter.format(paidIncome)}
                    </p>
                    <p className="text-sm font-semibold text-emerald-600 tabular-nums">
                      {currencyFormatter.format(paidIncome + predictedIncome)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Total de saída
                    </p>
                    <p className="text-lg font-semibold text-red-600 tabular-nums">
                      {currencyFormatter.format(paidOutcome)}
                    </p>
                    <p className="text-sm font-semibold text-red-600 tabular-nums">
                      {currencyFormatter.format(paidOutcome + predictedOutcome)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Resultado</p>
                    <p
                      className={`text-lg font-semibold tabular-nums ${
                        paidBalance > 0
                          ? "text-emerald-600"
                          : paidBalance < 0
                            ? "text-red-600"
                            : ""
                      }`}
                    >
                      {currencyFormatter.format(paidBalance)}
                    </p>
                    <p
                      className={`text-sm font-semibold tabular-nums ${
                        paidBalance + predictedBalance > 0
                          ? "text-emerald-600"
                          : paidBalance + predictedBalance < 0
                            ? "text-red-600"
                            : ""
                      }`}
                    >
                      {currencyFormatter.format(paidBalance + predictedBalance)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-semibold">Transações vinculadas</h2>
        <TransactionList
          transactions={transactionsData?.transactions || []}
          organizationSlug={currentOrg.slug}
          currentPersonId={personId}
          loading={transactionsLoading}
          onRefresh={() => {
            refetch()
            refetchTransactions()
            optimizedRefetch(
              [
                "accounts-summary",
                "transactions",
                "recent-transactions",
                "dashboard",
                "pending-transactions",
              ],
              currentOrg.slug,
            )
          }}
        />
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(nextOpen) => setAction(nextOpen ? "edit" : undefined)}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Pessoa</DialogTitle>
          </DialogHeader>
          <PersonForm
            person={person}
            organizationSlug={currentOrg.slug}
            onSuccess={handleEditSuccess}
            onCancel={() => setAction(undefined)}
          />
        </DialogContent>
      </Dialog>
    </div>
  )
}
